import { FirebaseApp, initializeApp } from "firebase/app";
import { GoogleAuthProvider, Unsubscribe, User, UserCredential, getAuth, signInWithEmailLink, signInWithRedirect } from "firebase/auth";
import { makeAutoObservable, runInAction } from "mobx";
import { logDebug, logError } from "../shared/common/logger";
import { AttachmentFile, IEmailMetaData, IFilledTicSheetData, IJobResults, ITokenArrays, LineType, TemplateMetaData } from "../shared/common/Azure";
import RestAPI from "../utils/restAPI";
import SharedUtils, { Roles } from "../shared/common/SharedUtils";

// Firebase config for data fetching (LineItem, General Page, etc.)
export const firebaseConfig = {
	// --- lasso mvp ----
	apiKey: "AIzaSyDgXXTHgXeATlP6Uz3WrhzO83t2cvg43Oc",
	authDomain: "golasso-ebc2f.firebaseapp.com",
	projectId: "golasso-ebc2f",
	storageBucket: "golasso-ebc2f.appspot.com",
	messagingSenderId: "72541849319",
	appId: "1:72541849319:web:4fc91bd3aeb7d7d5590442",
	measurementId: "G-SD7Q6W7FG4",
};

// Get the appropriate auth config based on environment
// TODO: Extract to env var (VITE_FIREBASE_AUTH_CONFIG) for production
export const getAuthFirebaseConfig = () => {
	const envConfig = import.meta.env.VITE_FIREBASE_AUTH_CONFIG;
	return envConfig ? JSON.parse(envConfig) : firebaseConfig;
};

interface ILassoUser extends User {
	organizationId: string;
	lassoUserId: string;
	role: string;
	isRegistered?: boolean;
	firstName?: string;
	lastName?: string;
}

class UserStore {
	public appReady = false;
	public user: ILassoUser | null = null;
	private _firebaseApp: FirebaseApp;
	private _unsubscribe: Unsubscribe;

	public get firebase() {
		return this._firebaseApp;
	}

	constructor() {
		logDebug("UserStore constructor");
		makeAutoObservable(this);

		this._firebaseApp = initializeApp(firebaseConfig);
		const auth = getAuth();
		this._unsubscribe = auth.onAuthStateChanged((user) => {
			if (user) {
				logDebug("LassoPage", user);
				this._setUser(user);
			} else {
				logDebug("no user");
				return;
				if (location.hostname === "tools.golasso.ai" || location.href.includes("tools=1")) {
					return;
				}
				const _href = location.href.toLowerCase();
				if (_href.includes("apikey=") && _href.includes("mode=signin") && _href.includes("email=")) {
					this._signIn();
				} else {
					if (location.pathname != "/") {
						window.localStorage.setItem("loginRedirect", location.href);
						location.href = "/";
					}
					this._setAppReady(true);
				}
			}
		});
	}

	private async setCookies(user: User) {
		const tok = await user.getIdToken();
		document.cookie = `idToken=${tok};max-age=3600;SameSite=Strict`;
		document.cookie = `idToken_uid=${user.uid};max-age=3600;SameSite=Strict`;
	}

	private async _signIn() {
		try {
			let email = window.localStorage.getItem("passwordless_email");
			window.localStorage.removeItem("passwordless_email");
			if (!email) {
				// read from url
				const url = new URL(location.href);
				email = url.searchParams.get("email");
				if (!email) {
					logError("no email to sign in with");
					this._setAppReady(true);
					// TODO :: notify the user
					return;
				}
			}
			const user = await signInWithEmailLink(getAuth(), email, location.href);
			logDebug("signed in", user);
			await this.setCookies(user.user!);
			const loginRedirect = window.localStorage.getItem("loginRedirect");
			if (loginRedirect) {
				window.localStorage.removeItem("loginRedirect");
				location.href = loginRedirect;
			} else {
				location.href = "/";
			}
		} catch (err) {
			logError("UserStore", err);
		}
	}
	public async signInWithRedirect(): Promise<UserCredential> {
		const provider = new GoogleAuthProvider();
		const user = await signInWithRedirect(getAuth(), provider);
		this.user = user;
		return user;
	}

	private _setAppReady(value: boolean) {
		runInAction(() => {
			this.appReady = value;
		});
	}

	get isUserAdmin() {
		return this.user?.role === Roles.ADMIN_ROLE;
	}
	get isUserCreator() {
		return this.user?.role === Roles.CREATOR_ROLE;
	}
	get isUserSimple() {
		return this.user?.role === Roles.USER_ROLE;
	}

	updateRegisteredUser(newUserData: { firstName: string; lastName: string; photoURL: string }) {
		if (!this.user) {
			logError("no user to update");
			return;
		}
		runInAction(() => {
			this.user = {
				...this.user!,
				...newUserData,
				isRegistered: true,
			};
		});
	}
	private async _setUser(user: User) {
		try {
			await this.setCookies(user);
			const userRole = await RestAPI.fetchAPI("/rest/access/getUserDetails", "GET", {}, "data", true);
			if (!userRole) {
				// TODO :: notify the user
				logError("no user role");
				this._setAppReady(true);
				return;
			}

			runInAction(() => {
				this.user = {
					...user,
					organizationId: userRole.organizationId,
					lassoUserId: userRole.lassoUserId,
					role: userRole.role,
					isRegistered: userRole.isRegistered,
					firstName: userRole.firstName,
					lastName: userRole.lastName,
					photoURL: userRole.photoURL,
				};
				logDebug("user with props", this.user);
			});
		} catch (err) {
			logError("UserStore", err);
			// TODO :: notify the user
		}
		this._setAppReady(true);
	}
}

class EstimateStore {
	public exportState: number = -1;
	public exportMessage: string = "";
	public estimateID: string = "";
	public processID: string = "";
	public pages: LineType.Page[] = [];
	public currentView: string = "Estimate";
	public ticSheetHighlight: number[] = [];
	public selectedPage: number = 0;
	public email: string = "yakir@golasso.ai";
	public phone: string = "(123) 456-7890";
	public insured: string = "Yakir Elkayam";
	public claimNumber: string = "12345678909876";
	public insuredLast: string = "Elkayam";
	public creatingPDF: boolean = false;
	// Today - 7 days;
	public dateContacted = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toLocaleDateString("en-US");
	public dateOfLoss = new Date(Date.now() - 10 * 24 * 60 * 60 * 1000).toLocaleDateString("en-US");
	public dateEstimateComplete = new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toLocaleDateString("en-US");
	public insuranceCarrier = "State Farm";
	public rawSheetData: LineType.Page[] = [];
	public templateMetaData: TemplateMetaData | null = null;
	public filledTicSheetData: IFilledTicSheetData[][] = [];
	public jobResults: IJobResults | null = null;
	public pageImageURL: string | undefined = "";
	public allFiles: AttachmentFile[] = [];
	private _netClaim: number = 123456;
	public dimensions: { w: number; h: number }[] = Array(100).fill({ w: 0, h: 0 });
	public updateLineItemExtracted(page: number, index: number, key?: ITokenArrays, value?: ITokenArrays, selected?: ITokenArrays) {
		value && (this.pages[page].lineItems[index].matchCell!.templateTuple.meta!.extracted!.extractedValueInput = value!);
		key && (this.pages[page].lineItems[index].matchCell!.templateTuple.meta!.extracted!.extractedKeyInput = key!);
		selected && (this.pages[page].lineItems[index].matchCell!.templateTuple.meta!.extracted!.extractedSelectedWords = selected!);
	}

	public setEmailMeta(meta?: IEmailMetaData) {
		if (!meta) return;
		this.email = meta.email;
		this.phone = meta.phone;
		this.insured = meta.insured;
		this.claimNumber = meta.claim;
		this.insuredLast = this.insured.split(/\s+/).pop() ?? "";
		// this.insuredLast = meta.insuredLast;
		// this.dateContacted = meta.dateContacted;
		// this.dateOfLoss = meta.dateOfLoss;
		// this.dateEstimateComplete = meta.dateEstimateComplete;
		// this.insuranceCarrier = meta.insuranceCarrier;
	}

	setCreatingPDF(value: boolean) {
		this.creatingPDF = value;
	}

	public setAllFiles(value: AttachmentFile[]) {
		this.allFiles = value;
	}

	public setFilledTicSheetData(value: IFilledTicSheetData[][]) {
		this.filledTicSheetData = value;
	}

	public setJobResults(value: IJobResults) {
		this.jobResults = value;
	}

	public setRawSheetData(rawSheetData: LineType.Page[]) {
		this.rawSheetData = rawSheetData;
	}

	public setTemplateMetaData(templateMetaData: TemplateMetaData) {
		this.templateMetaData = templateMetaData;
	}

	public get netClaim() {
		return this._netClaim;
	}

	constructor() {
		logDebug("EstimateStore constructor");
		makeAutoObservable(this);
	}

	public setRoomDimensions(index: number, dimensions: { w: number; h: number }) {
		this.dimensions[index] = { ...dimensions };
	}

	public calculateTotal() {
		//@ts-ignore
		this._netClaim = Array.from(document.querySelectorAll(".cellIndex-7"))
			//@ts-ignore
			.map((val) => val.innerText)
			.map((val) => parseFloat(val))
			.reduce((a, b) => a + b, 0);
	}
	public getRelativePageIndex(page: number) {
		if (!this.jobResults) return page;
		if (this.jobResults.pdfResults.length === 0) return page;
		let index = 0;
		let runningIndex = 0;
		do {
			const _runningIndex = runningIndex + this.jobResults!.pdfResults[index].pages.length;
			if (page < _runningIndex) {
				return page - runningIndex;
			}
			runningIndex = _runningIndex;
		} while (index < this.jobResults!.pdfResults.length);
		return page;
	}

	public getPdfIdAndPageFromPageIndex(page: number): {
		pdfID?: string;
		page?: LineType.Page;
	} {
		if (!this.jobResults) return {};
		if (this.jobResults.pdfResults.length === 0) return {};
		let index = 0;
		let runningIndex = 0;
		do {
			const _runningIndex = runningIndex + this.jobResults!.pdfResults[index].pages.length;
			if (page < _runningIndex) {
				return {
					pdfID: this.jobResults!.pdfResults[index].pdfID,
					page: this.jobResults!.pdfResults[index].pages[page - runningIndex],
				};
			}
			index++;
			runningIndex = _runningIndex;
		} while (index < this.jobResults!.pdfResults.length);
		return {};
	}

	public setCurrentView(view: string, pageImageURL?: string, highlight?: number[]) {
		this.currentView = view;
		this.pageImageURL = pageImageURL;
		this.ticSheetHighlight = highlight ?? [];
	}

	public addLineItem(index: number, lineItem: LineType.Line) {
		this.pages[index].lineItems.push(lineItem);
	}
	public setEstimateID(estimateID: string) {
		this.estimateID = estimateID;
	}

	public setProcessID(processID: string) {
		this.processID = processID;
	}

	public setPages(pages: LineType.Page[]) {
		this.pages = pages;
	}

	public setSelectPage(page: number) {
		this.selectedPage = page;
	}

	public getDebris(page: number) {
		if (!this.pages.length) return [];
		return this.pages[page].header?.debris;
	}

	public get address() {
		if (!this.pages.length) return "";
		return this.pages[0].header?.address
			.map((c) => c.content)
			.join(" ")
			.replace("Address", "")
			.replaceAll(":", "")
			.trim();
	}

	public get cityAndState() {
		return "New York, NY, 10001";
	}

	public get roomNames() {
		return this.pages.map((page) => {
			const roomName = page.header?.room
				.map((c) => c.content)
				.join(" ")
				.replace("ROOM", "")
				.replace(":", "")
				.replace(";", "")
				.trim();
			if (!roomName) {
				return "Missing Room Name";
			}
			if (roomName?.length < 3) {
				return "Missing Room Name";
			}
			return roomName;
		});
	}

	public get estimateRef() {
		if (!this.pages.length) return "";
		let dash =
			this.pages[0].header?.dash
				.map((c) => c.content)
				.join(" ")
				.replace("Dash", "")
				.replaceAll("#", "") ?? "";
		// remove all empty spaces
		dash = dash.replace(/\s/g, "");
		return dash;
	}
}

const _userStore: UserStore = new UserStore();
const getUserStore = () => _userStore;

const _estimateStore: EstimateStore = new EstimateStore();
const getEstimateStore = () => _estimateStore;

export const debugStyle = (style: string, color: string) => {
	const s = style + (SharedUtils.showDebug ? ` ${color}` : "");
	// logDebug('s', s, style);
	return s;
};

export { getEstimateStore, getUserStore };
